import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Group from 'components/utils/Group'
import { pluralizeToString } from 'lib/string/pluralize'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import AssistChip from 'LuxKit/Chips/AssistChip'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ModalContext from 'contexts/ModalContext'
import TravellersModal from '../shared/travellers/Modal'
import RoomsModal from '../shared/rooms/Modal'
import { searchSteps } from 'components/SearchV2/Components/Mobile/TabContents/HotelsTabContent'
import { useMultiRoomSelectionSearchBar } from 'businessTraveller/hooks/useMultiRoomSelection'

interface Props {
  className?: string;
  activeStep: searchSteps | undefined
  openEmployeeMenu: () => void
  openRoomMenu: () => void
  openLocationMenu: () => void
}

function BusinessTravellerSelectHotelsMobile(props: Props) {
  const {
    activeStep,
    className,
    openRoomMenu,
    openLocationMenu,
  } = props

  const {
    travellerEmployees,
    totalTravellers,
    roomCount,
  } = useMultiRoomSelectionSearchBar()

  const localInputRef = useRef<HTMLButtonElement>(null)
  const roomInputRef = useRef<HTMLButtonElement>(null)
  const [error, setError] = useState<string>()

  const fieldValue = useMemo(() => {
    if (totalTravellers === 0) {
      return undefined
    }
    const primaryTraveller = travellerEmployees[0]
    if (!primaryTraveller) {
      return `${totalTravellers} guests`
    }
    if (totalTravellers === 1) {
      return `${primaryTraveller.firstName} ${primaryTraveller.lastName}`
    }
    return `${primaryTraveller.firstName} ${primaryTraveller.lastName} +${totalTravellers - 1} more`
  }, [travellerEmployees, totalTravellers])

  // Open room menu after selecting travellers
  const handleSelectTravellerApply = useCallback(() => {
    openRoomMenu()
  }, [openRoomMenu])

  const handleSelectRoomApply = useCallback(() => {
    openLocationMenu()
  }, [openLocationMenu])

  const showModal = useContext(ModalContext)

  const showBusinessTravellerSelectTravellersModal = useCallback(async() => {
    await showModal(<TravellersModal onApply={handleSelectTravellerApply} useSelection={useMultiRoomSelectionSearchBar} />)
  }, [showModal, handleSelectTravellerApply])

  const showBusinessTravellerSelectRoomsModal = useCallback(async() => {
    await showModal(<RoomsModal onApply={handleSelectRoomApply} useSelection={useMultiRoomSelectionSearchBar} />)
  }, [showModal, handleSelectRoomApply])

  useEffect(() => {
    if (activeStep === searchSteps.GUESTS) {
      showBusinessTravellerSelectTravellersModal()
    } else if (activeStep === searchSteps.MULTI_ROOMS) {
      showBusinessTravellerSelectRoomsModal()
    }
  }, [activeStep, showBusinessTravellerSelectTravellersModal, showBusinessTravellerSelectRoomsModal])

  return (
    <SearchFormFieldGroup className={className}>
      <HiddenInput
        value={fieldValue}
        name="travellers"
        required
        invalidMessage="Please select traveller"
        onErrorUpdate={setError}
      />
      <Group direction="horizontal" gap={8} wrap="wrap">
        <Group direction="horizontal" gap={12}>
          <AssistChip
            variant="outline"
            size="medium"
            startIcon={<LineUserIcon />}
            endIcon={<LineAngleDownIcon />}
            onClick={showBusinessTravellerSelectTravellersModal}
            ref={localInputRef}
            error={!!error}
          >
            {fieldValue && <>Travellers: <b>{fieldValue}</b></>}
            {!fieldValue && 'Add travellers'}
          </AssistChip>
        </Group>
        <Group direction="horizontal" gap={12}>
          <AssistChip
            variant="outline"
            size="medium"
            endIcon={<LineAngleDownIcon />}
            onClick={showBusinessTravellerSelectRoomsModal}
            ref={roomInputRef}
          >
            {pluralizeToString('room', roomCount)}
          </AssistChip>
        </Group>
      </Group>
    </SearchFormFieldGroup>
  )
}

export default BusinessTravellerSelectHotelsMobile
